// src/components/chat/InfoBanner.tsx
import * as React from "react";

export interface InfoBannerProps {
  message: string | null;
  variant?: "info" | "warning";
  onDismiss?: () => void;
  autoDismissMs?: number;
}

const VARIANT_STYLES = {
  info: {
    backgroundColor: "#e6f2ff",
    color: "#0078d4",
    borderColor: "#b3d7ff",
    shadowColor: "rgba(0, 120, 212, 0.1)",
    stroke: "#0078d4",
  },
  warning: {
    backgroundColor: "#fff4ce",
    color: "#797673",
    borderColor: "#ffe7a0",
    shadowColor: "rgba(121, 118, 115, 0.1)",
    stroke: "#797673",
  },
};

const InfoBanner: React.FC<InfoBannerProps> = ({
  message,
  variant = "info",
  onDismiss,
  autoDismissMs,
}) => {
  React.useEffect(() => {
    if (!message || !autoDismissMs || !onDismiss) return;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [message, autoDismissMs, onDismiss]);

  if (!message) return null;

  const colors = VARIANT_STYLES[variant];

  const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px",
    backgroundColor: colors.backgroundColor,
    borderRadius: "4px",
    color: colors.color,
    fontSize: "14px",
    fontFamily: "'Segoe UI', sans-serif",
    border: `1px solid ${colors.borderColor}`,
    boxShadow: `0 1px 3px ${colors.shadowColor}`,
  };

  const closeButtonStyle: React.CSSProperties = {
    marginLeft: "auto",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    padding: "0",
    opacity: 0.7,
    transition: "opacity 0.2s",
  };

  return (
    <div style={containerStyle}>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8" r="7.5" stroke={colors.stroke} />
          <path d="M8 5V8.5" stroke={colors.stroke} strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="11.5" r="1" fill={colors.stroke} />
        </svg>
      </div>
      <span style={{ flex: 1 }}>{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={closeButtonStyle}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
          aria-label="Dismiss notification"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4L12 12M12 4L4 12"
              stroke={colors.stroke}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InfoBanner;
